import { useWizard } from '@heidi/flow/react';
import { effectiveSteps } from '@heidi/flow/wizard';
import { LanguageCode } from '@heidi/schema/ml-scribe';
import { useIsMobile } from '@heidi/utils';
import { type CountryType } from '@heidi/utils/countries';
import { getCountry } from '@heidi/utils/get-country';
import {
  getAvailableDisplayLanguagesForCountry,
  shouldShowLanguageDropdown,
} from '@heidi/utils/i18n';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { isMobile as isMobileDevice } from 'react-device-detect';
import type { OnboardingFlowKind, PersonalizationStep } from '../../../contracts';
import { getOnboardingHost } from '../onboarding-host';
import { personalizationWizard, PROGRESS_HIDDEN_STEPS } from '../personalization.wizard';
import type { PersonalizationFormData } from '../personalizationTypes';
import { isStepValidForCurrentStep } from '../stepRegistry';
import { clearAuthOnboardAnswers } from '../utils/authOnboardAnswers';
import {
  clearOnboardingProgress,
  readOnboardingProgress,
  saveOnboardingCountryAsUserSet,
  saveOnboardingFormData,
  saveOnboardingWizardProgress,
} from '../utils/onboardingProgressPersistence';
import { useIsOnboardingPreviewHidden } from './useIsOnboardingPreviewHidden';

/**
 * Auto-select the primary language for a country.
 * Falls back to English for unmapped countries.
 */
const getInitialDisplayLanguage = (country: CountryType | null): LanguageCode => {
  const langs = getAvailableDisplayLanguagesForCountry(country?.countryCode);
  return langs[0] ?? LanguageCode.en;
};

// Timezone guess for immediate first paint; upgraded async via getCountryFromGeo.
const getInitialFormData = (): PersonalizationFormData => {
  const country = getCountry();
  return {
    firstName: '',
    lastName: '',
    specialty: null,
    otherSpecialty: '',
    role: null,
    country,
    jurisdiction: '', // JurisdictionSelect will auto-detect based on country
    displayLanguage: getInitialDisplayLanguage(country),
    phoneNumber: '',
    clinicName: '',
    isOfficialPractice: undefined,
    cqcPracticeId: undefined,
    clinicSize: null,
    emrEhr: null,
    templateDescription: '',
    generatedTemplateId: null,
    generatedTemplateName: null,
    generatedTemplateStructure: null,
    ctaOptIn: false,
    demoReason: '',
    bookingIntent: null,
    inviteEmails: [],
  };
};

function getDefaultStartStep(flowKind: OnboardingFlowKind): PersonalizationStep {
  return flowKind === 'learn-basics' ? 'about-you' : 'name';
}

// DEV ONLY: Get initial step from URL param for testing
// Usage: Add ?step=document-selection to URL to skip to that step
const getDevInitialStep = (flowKind: OnboardingFlowKind): PersonalizationStep => {
  const fallback = getDefaultStartStep(flowKind);
  if (typeof window === 'undefined') return fallback;
  if (getOnboardingHost().isProductionBuild()) return fallback;

  const params = new URLSearchParams(window.location.search);
  const stepParam = params.get('step') as PersonalizationStep | null;

  // Any declared step is accepted; if it isn't enabled for this variant, the
  // wizard reconciles it to the nearest surviving step on start.
  if (stepParam && personalizationWizard.stepIds.includes(stepParam)) {
    // eslint-disable-next-line no-console
    console.log(`[DEV] Skipping to step: ${stepParam}`);
    return stepParam;
  }
  return fallback;
};

// DEV ONLY: Pre-fill form data when skipping steps for testing
const getDevFormData = (flowKind: OnboardingFlowKind): PersonalizationFormData => {
  const baseData = getInitialFormData();

  if (typeof window === 'undefined') return baseData;
  if (getOnboardingHost().isProductionBuild()) return baseData;

  const params = new URLSearchParams(window.location.search);
  const stepParam = params.get('step') as PersonalizationStep | null;
  const defaultStep = getDefaultStartStep(flowKind);

  // If skipping to a later step, pre-fill with dev data
  if (stepParam && stepParam !== defaultStep) {
    return {
      ...baseData,
      firstName: 'Dev',
      lastName: 'User',
      clinicName: 'Dev Clinic',
    };
  }

  return baseData;
};

export interface UsePersonalizationFormOptions {
  flowKind?: OnboardingFlowKind;
  /**
   * The /auth/onboard page already collected everything up to `clinic`, so
   * those steps are disabled and the flow opens on the first step after them.
   * Read once at mount.
   */
  resumeAfterAuthOnboard?: boolean;
  /**
   * Fields already saved on the user, merged over the initial form data when
   * resuming after the /auth/onboard steps. Read once at mount.
   */
  savedUserSeed?: Partial<PersonalizationFormData>;
}

export function usePersonalizationForm(options: UsePersonalizationFormOptions = {}) {
  const { flowKind = 'v2' } = options;
  const [resumeAfterAuthOnboard] = useState(
    () => options.resumeAfterAuthOnboard ?? false
  );
  // Mobile flow skips template creation / mic permissions because those steps
  // render previews/UX that don't work on mobile web. We OR two signals:
  //   1. `react-device-detect.isMobile` (UA-based) catches real phones/tablets
  //      regardless of viewport (iPhone landscape, iPad portrait, etc.).
  //   2. `useIsMobile` (viewport < 640px) is a safety net for edge cases
  //      (spoofed UAs, desktop DevTools emulation, narrow resized windows).
  const isMobileViewport = useIsMobile();
  const isMobile = isMobileDevice || isMobileViewport;
  // Learn-basics only: below `lg` (1024px) the preview panel is hidden, so the
  // mic-test / note-edit steps can't function. This trims that flow to the
  // compact details-only path without affecting the v2 flow's 640px behaviour.
  const isPreviewHidden = useIsOnboardingPreviewHidden();
  const host = getOnboardingHost();
  const isResumeEnabled = host.useIsOnboardingResumeEnabled();
  // Read once for the actor's lifetime so the restored step and the restored
  // fields come from the same record.
  const [restoredProgress] = useState(() =>
    isResumeEnabled ? readOnboardingProgress<PersonalizationFormData>(flowKind) : null
  );
  // The kill switch also has to stop the writing, not just the reading — a
  // record left behind would hold the clinician's details for the rest of the
  // tab's life with the feature off.
  useEffect(() => {
    if (!isResumeEnabled) {
      clearOnboardingProgress();
      clearAuthOnboardAnswers();
    }
  }, [isResumeEnabled]);
  const [formData, setFormData] = useState<PersonalizationFormData>(() => ({
    ...getDevFormData(flowKind),
    ...restoredProgress?.formData,
    ...(resumeAfterAuthOnboard && options.savedUserSeed),
  }));
  const { markCountryAsUserSet: markGeoCountryAsUserSet } =
    host.useOnboardingGeoCountryDefault(setFormData, {
      hasUserSetCountry: restoredProgress?.hasUserSetCountry ?? false,
    });
  const markCountryAsUserSet = useCallback(() => {
    markGeoCountryAsUserSet();
    if (isResumeEnabled) saveOnboardingCountryAsUserSet(flowKind);
  }, [flowKind, isResumeEnabled, markGeoCountryAsUserSet]);
  // Legacy skip-origin marker — kept for analytics/context consumers; skip
  // handlers advance one step via `goToNextStep` and Back uses `canGoBack`.
  const [skipOriginStep, setSkipOriginStep] = useState<PersonalizationStep | null>(null);

  const isLearnBasicsPreviewFlow =
    flowKind === 'learn-basics' && !isMobile && !isPreviewHidden;
  // The guided tour is an expansion of the sample-conversation flow — the tour
  // screens depend on the sample note/transcript it provides. Below `lg` there
  // is no preview pane to tour, so those users are outside the experiment
  // entirely; folding that in here keeps every consumer (steps, previews,
  // exposure) agreeing.
  const isGuidedTourEnabled = host.useIsOnboardingGuidedTourEnabled();
  const isGuidedTourEligible = isLearnBasicsPreviewFlow;
  const isGuidedTour = isGuidedTourEligible && isGuidedTourEnabled;
  const showsInviteTeam =
    !!host.isPracticeInviteFlowEnabled() &&
    !host.isAppleCompliantBuild() &&
    formData.clinicSize != null &&
    formData.clinicSize !== 'only-me';

  const guards = {
    showsAboutYou: () => flowKind === 'learn-basics' && !resumeAfterAuthOnboard,
    showsNameAndRole: () => flowKind === 'v2' && !resumeAfterAuthOnboard,
    showsClinic: () => !resumeAfterAuthOnboard,
    isV2DesktopFlow: () => flowKind === 'v2' && !isMobile,
    // Invite step collects emails before completion; send waits for the
    // organisation_info PATCH (BE auto-creates Practice team). Gate matches
    // that cohort + Apple IAP (no team-billing CTAs).
    showsInviteTeam: () => showsInviteTeam && !isGuidedTour,
    // Same cohort, earlier slot — the guided-tour arm invites right after
    // clinic instead of just before completion.
    showsGuidedTourInviteTeam: () => showsInviteTeam && isGuidedTour,
    showsMicPermissions: () => flowKind === 'v2' && !isMobile,
    showsDocumentSelection: () => flowKind === 'v2',
    isLearnBasicsPreviewFlow: () => isLearnBasicsPreviewFlow,
    // note-ready / note-edit belong to the control arm only; the guided tour
    // replaces them with the three tour screens.
    isControlNotePreviewFlow: () => isLearnBasicsPreviewFlow && !isGuidedTour,
    isGuidedTourFlow: () => isGuidedTour,
  };
  // The entry step varies by variant; frozen for the actor's lifetime.
  const [initialStep] = useState(() =>
    resumeAfterAuthOnboard
      ? (effectiveSteps(personalizationWizard, guards)[0] ??
        getDefaultStartStep(flowKind))
      : getDevInitialStep(flowKind)
  );

  const wizardState = useWizard(personalizationWizard, {
    guards,
    persistence: {
      save: (snapshot) => {
        if (isResumeEnabled) saveOnboardingWizardProgress(flowKind, snapshot);
      },
      restore: () => restoredProgress?.wizard ?? null,
      clear: clearOnboardingProgress,
    },
    input: { step: initialStep },
  });

  // Every field edit rewrites the record, so a refresh mid-step keeps the
  // partially-filled step rather than only the last completed one.
  useEffect(() => {
    if (!isResumeEnabled) return;
    saveOnboardingFormData(flowKind, formData);
  }, [flowKind, formData, isResumeEnabled]);

  const currentStep = wizardState.step;
  // Consumers (and useTemplateGeneration's props) still type this mutable.
  const steps = useMemo(() => [...wizardState.steps], [wizardState.steps]);

  const updateFormData = useCallback(
    <K extends keyof PersonalizationFormData>(
      field: K,
      value: PersonalizationFormData[K]
    ) => {
      if (field === 'country') {
        markCountryAsUserSet();
      }
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    [markCountryAsUserSet]
  );

  const updateMultipleFields = useCallback(
    (updates: Partial<PersonalizationFormData>) => {
      if ('country' in updates) {
        markCountryAsUserSet();
      }
      setFormData((prev) => ({ ...prev, ...updates }));
    },
    [markCountryAsUserSet]
  );

  const currentStepIndex = wizardState.stepNumber - 1;
  const totalSteps = wizardState.totalSteps;

  const progressSteps = useMemo(
    () => steps.filter((step) => !PROGRESS_HIDDEN_STEPS.has(step)),
    [steps]
  );

  const progressStepIndex = (() => {
    const direct = progressSteps.indexOf(currentStep);
    if (direct >= 0) return direct;
    const flowIndex = steps.indexOf(currentStep);
    for (let i = flowIndex - 1; i >= 0; i--) {
      const idx = progressSteps.indexOf(steps[i] as PersonalizationStep);
      if (idx >= 0) return idx;
    }
    return 0;
  })();
  const progressPercentage = ((progressStepIndex + 1) / progressSteps.length) * 100;

  const goToNextStep = useCallback(() => {
    // Completion is consumer-owned (`handleContinue` → `onComplete`): the
    // wizard's done-state is intentionally never reached, matching the old
    // index-bound guard.
    if (wizardState.stepNumber >= wizardState.totalSteps) return false;
    return wizardState.next();
  }, [wizardState]);

  const goToPreviousStep = useCallback(() => wizardState.back(), [wizardState]);

  // Go to a specific step (used for skip flow and auto-transitions)
  const goToStep = useCallback(
    (step: PersonalizationStep) => {
      wizardState.goTo(step);
    },
    [wizardState]
  );

  const canGoBack = wizardState.canGoBack;

  const isLastStep = wizardState.stepNumber >= wizardState.totalSteps;

  const availableDisplayLanguages = useMemo(
    () => getAvailableDisplayLanguagesForCountry(formData.country?.countryCode),
    [formData.country?.countryCode]
  );

  const showLanguageDropdown = useMemo(
    () => shouldShowLanguageDropdown(formData.country?.countryCode),
    [formData.country?.countryCode]
  );

  const isCurrentStepValid = isStepValidForCurrentStep(currentStep, formData);

  // Get user initials for preview
  const getInitials = () => {
    const firstInitial = formData.firstName.charAt(0).toUpperCase();
    const lastInitial = formData.lastName.charAt(0).toUpperCase();
    return firstInitial + lastInitial;
  };

  // Get full name for preview
  const getFullName = () => {
    const parts = [formData.firstName, formData.lastName].filter(Boolean);
    return parts.join(' ');
  };

  return {
    flowKind,
    formData,
    updateFormData,
    updateMultipleFields,
    currentStep,
    /** Direct step assignment — prefer goToNextStep/goToPreviousStep/goToStep. */
    setCurrentStep: goToStep,
    skipOriginStep,
    setSkipOriginStep,
    currentStepIndex,
    totalSteps,
    steps,
    progressPercentage,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    canGoBack,
    isLastStep,
    isCurrentStepValid,
    availableDisplayLanguages,
    showLanguageDropdown,
    getInitials,
    getFullName,
    isMobile,
    /**
     * Guided-tour arm (`release-new-onboarding-flow` = `experimental-3` AND a
     * preview-capable viewport). Resolved here rather than re-derived by
     * consumers so the wizard guards and the rendered screens can never
     * disagree about which arm the user is in.
     */
    isGuidedTour,
    /**
     * In the cohort the guided-tour experiment runs on, regardless of which arm
     * the flag resolved to. Analytics uses this to decide whether an arm is
     * reportable at all, so ineligible users stay unlabelled instead of padding
     * the control arm.
     */
    isGuidedTourEligible,
    /**
     * Learn-basics on a viewport wide enough for the preview pane — the cohort
     * the guided-tour experiment can run on at all. Exposed so exposure
     * tracking fires for both arms and for neither below `lg`.
     */
    isLearnBasicsPreviewFlow,
  };
}

export type UsePersonalizationFormReturn = ReturnType<typeof usePersonalizationForm>;
